/**
 * Центр доверия (17.11) — главный антиконфликтный экран.
 *
 * Здесь собрано всё, что иначе выясняется вопросами вслух: кто имеет доступ,
 * что подвисло, что менялось и кем. Обе стороны видят одно и то же — это и
 * есть смысл раздела.
 */

import { useEffect, useState } from 'react'

import { IncidentSheet } from '@/features/incidents/IncidentSheet'
import { track } from '@/shared/analytics'
import {
  SEVERITY_LABELS,
  useAcknowledgeIncident,
  useIncidents,
  useTrust,
  type Incident,
} from '@/shared/api/trust'
import { formatRelative, formatTime, plural } from '@/shared/format'
import { Button } from '@/shared/ui/Button'
import { Icon } from '@/shared/ui/Icon'
import { Sheet } from '@/shared/ui/Sheet'
import { Field, TextInput } from '@/shared/ui/fields'
import { Avatar, Card, Chip, Money, Row, SectionTitle } from '@/shared/ui/primitives'
import { SkeletonCard } from '@/shared/ui/states'
import { toast } from '@/shared/ui/toast'

export function TrustPanel({
  familyId,
  timeZone,
  canReport,
  canAcknowledge,
}: {
  familyId: number
  timeZone: string
  canReport: boolean
  canAcknowledge: boolean
}) {
  const [reportOpen, setReportOpen] = useState(false)
  const [acking, setAcking] = useState<Incident | null>(null)

  const trust = useTrust(familyId)
  const incidents = useIncidents(familyId)

  if (trust.isLoading) return <SkeletonCard lines={4} />
  const state = trust.data
  if (!state) return null

  const attention = [
    {
      count: state.open_shifts,
      label: plural(state.open_shifts, 'незакрытая смена', 'незакрытые смены', 'незакрытых смен'),
    },
    {
      count: state.pending_expenses,
      label: plural(
        state.pending_expenses,
        'расход ждёт решения',
        'расхода ждут решения',
        'расходов ждут решения',
      ),
    },
    {
      count: state.open_disputes,
      label: plural(
        state.open_disputes,
        'открытый вопрос по расчёту',
        'открытых вопроса по расчёту',
        'открытых вопросов по расчёту',
      ),
    },
    {
      count: state.unacknowledged_incidents,
      label: plural(
        state.unacknowledged_incidents,
        'происшествие без отметки',
        'происшествия без отметки',
        'происшествий без отметки',
      ),
    },
  ].filter((item) => item.count > 0)

  return (
    <>
      {attention.length > 0 ? (
        <Card className="bg-warm-soft">
          <p className="text-warm-ink text-body font-semibold">Требует внимания</p>
          <ul className="mt-2 space-y-1">
            {attention.map((item) => (
              <li key={item.label} className="text-note">
                · {item.count} {item.label}
              </li>
            ))}
          </ul>
        </Card>
      ) : (
        <Card className="bg-success-soft">
          <p className="text-success text-body font-semibold">
            {/* Значок вынесен из подписи: иначе скринридер читает галочку как слово. */}
            <Icon name="state-success" size={18} className="mr-1.5 align-[-4px]" />
            Всё под контролем
          </p>
          <p className="text-text-muted mt-1 text-note leading-snug">
            Ничего не подвисло: смены закрыты, расходы решены, вопросов нет.
          </p>
        </Card>
      )}

      <div>
        <SectionTitle>Кто видит данные</SectionTitle>
        <Card padded={false} className="px-4">
          {state.members.map((member, index) => (
            <div key={member.user_id}>
              {index > 0 && <div className="bg-line h-px" />}
              <Row
                icon={<Avatar name={member.name} />}
                title={member.name}
                subtitle={member.role === 'parent' ? 'родитель' : 'няня'}
                right={
                  member.role === 'nanny' && state.geo_enabled ? (
                    <Chip tone={member.geo_consent ? 'success' : 'neutral'} dot>
                      {member.geo_consent ? 'геометки вкл' : 'геометки выкл'}
                    </Chip>
                  ) : undefined
                }
              />
            </div>
          ))}
        </Card>
        <p className="text-text-muted mt-2 px-1 text-caption leading-snug">
          Фото, заметки и геометки видят только эти люди. Никому больше данные не
          передаются.
        </p>
      </div>

      {state.last_shift_end && (
        <Card>
          <div className="flex items-baseline justify-between">
            <span className="text-text-muted text-body">Последняя смена</span>
            <span className="text-body font-medium">
              {formatRelative(state.last_shift_end)}
            </span>
          </div>
        </Card>
      )}

      {/* Происшествия */}
      <div>
        <SectionTitle
          action={
            canReport ? (
              <button
                type="button"
                onClick={() => setReportOpen(true)}
                className="text-accent -my-3 min-h-[44px] px-1 py-3 text-caption font-medium"
              >
                Сообщить
              </button>
            ) : undefined
          }
        >
          Происшествия
        </SectionTitle>
        {incidents.data && incidents.data.length > 0 ? (
          <div className="space-y-2">
            {incidents.data.map((incident) => (
              <IncidentCard
                key={incident.id}
                incident={incident}
                timeZone={timeZone}
                onAcknowledge={canAcknowledge ? setAcking : undefined}
              />
            ))}
          </div>
        ) : (
          <Card>
            <p className="text-text-muted text-note leading-snug">
              Записей нет. Это раздел на всякий случай: если что-то случится, здесь
              останется фиксация — что произошло и что сделали.
            </p>
          </Card>
        )}
      </div>

      {/* Журнал изменений */}
      {state.changes.length > 0 && (
        <div>
          <SectionTitle>Что менялось</SectionTitle>
          <Card padded={false} className="px-4 py-1">
            {state.changes.map((change, index) => (
              <div
                key={`${change.action}-${change.at}-${index}`}
                className="border-line border-b py-3 last:border-b-0"
              >
                <p className="text-note">
                  <span className="font-medium">{change.actor_name}</span> {change.label}
                </p>
                <p className="text-text-muted mt-0.5 text-caption">
                  {formatRelative(change.at)}
                  {change.reason && ` · ${change.reason}`}
                </p>
              </div>
            ))}
          </Card>
          <p className="text-text-muted mt-2 px-1 text-caption leading-snug">
            История не удаляется. В спорной ситуации видно, кто и когда что поправил.
          </p>
        </div>
      )}

      <IncidentSheet
        open={reportOpen}
        familyId={familyId}
        onClose={() => setReportOpen(false)}
      />

      <AckSheet incident={acking} familyId={familyId} onClose={() => setAcking(null)} />
    </>
  )
}

function IncidentCard({
  incident,
  timeZone,
  onAcknowledge,
}: {
  incident: Incident
  timeZone: string
  onAcknowledge?: (incident: Incident) => void
}) {
  const tone =
    incident.severity === 'serious'
      ? 'danger'
      : incident.severity === 'medium'
        ? 'warm'
        : 'neutral'

  return (
    <Card>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Chip tone={tone}>{SEVERITY_LABELS[incident.severity]}</Chip>
          <p className="mt-2 text-body font-semibold">{incident.title}</p>
        </div>
        <span className="text-text-muted tabular shrink-0 text-caption">
          {formatTime(incident.occurred_at, timeZone)}
        </span>
      </div>

      <p className="mt-2 text-note leading-snug">{incident.description}</p>

      {incident.action_taken && (
        <div className="bg-surface-muted mt-3 rounded-[var(--radius-tile)] p-3">
          <p className="text-text-muted text-caption font-semibold uppercase">
            что сделали
          </p>
          <p className="mt-1 text-note leading-snug">{incident.action_taken}</p>
        </div>
      )}

      {incident.thumb_urls.length > 0 && (
        <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
          {incident.thumb_urls.map((url) => (
            <img
              key={url}
              src={url}
              alt=""
              loading="lazy"
              className="size-20 shrink-0 rounded-[var(--radius-thumb)] object-cover"
            />
          ))}
        </div>
      )}

      <div className="border-line mt-3 border-t pt-3">
        {incident.acknowledged ? (
          <>
            <p className="text-success text-caption">
              <Icon name="state-success" size={16} className="mr-1 align-[-3px]" />
              {incident.acknowledged_by_name ?? 'Родитель'} ознакомился(ась)
            </p>
            {incident.parent_comment && (
              <p className="text-text-muted mt-1 text-note leading-snug">
                {incident.parent_comment}
              </p>
            )}
          </>
        ) : onAcknowledge ? (
          <Button block variant="secondary" onClick={() => onAcknowledge(incident)}>
            Ознакомлен(а)
          </Button>
        ) : (
          <p className="text-text-muted text-caption">Ждём отметки родителей</p>
        )}
      </div>
    </Card>
  )
}

function AckSheet({
  incident,
  familyId,
  onClose,
}: {
  incident: Incident | null
  familyId: number
  onClose: () => void
}) {
  const [comment, setComment] = useState('')

  const ack = useAcknowledgeIncident(familyId, {
    onSuccess: () => {
      toast.success('Отмечено, няня увидит')
      setComment('')
      onClose()
    },
    onError: (error) => toast.error(error.message),
  })

  return (
    <Sheet
      open={incident !== null}
      onClose={() => {
        setComment('')
        onClose()
      }}
      title={incident ? incident.title : ''}
      footer={
        <Button
          block
          size="lg"
          loading={ack.isPending}
          onClick={() =>
            incident && ack.mutate({ id: incident.id, comment: comment.trim() || undefined })
          }
        >
          Ознакомлен(а)
        </Button>
      }
    >
      <Field
        label="Комментарий"
        hint="Не обязателен, но одна строка «спасибо, что сразу написали» стоит дорого."
      >
        <TextInput
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          placeholder="Спасибо, что сразу написали"
          maxLength={200}
        />
      </Field>
    </Sheet>
  )
}

/** Итог дня (17.9) — день целиком, без пролистывания ленты. */
export function DaySummaryCard({
  summary,
  currency,
}: {
  summary: {
    minutes: number
    amount: string
    events: number
    photos: number
    expenses_count: number
    expenses_amount: string
    geo_points: number
    tasks_done: number
    tasks_left: number
    incidents: number
    shift_open: boolean
  }
  currency: string
}) {
  const nothing =
    summary.events === 0 && summary.minutes === 0 && summary.expenses_count === 0

  // Итог дня — обещание продукта «весь день одним взглядом». Метрика меряет
  // именно его: показали карточку — значит, обещание сработало.
  useEffect(() => {
    if (!nothing) track('daily_summary_viewed', { has_incidents: summary.incidents > 0 })
  }, [nothing, summary.incidents])

  if (nothing) return null

  // Склонения важны: «1 задач выполнено» читается как недоделка в продукте.
  const lines: string[] = []
  if (summary.events > 0) {
    lines.push(`${summary.events} ${plural(summary.events, 'событие', 'события', 'событий')}`)
  }
  if (summary.photos > 0) lines.push(`${summary.photos} фото`)
  if (summary.tasks_done > 0) {
    lines.push(
      `${summary.tasks_done} ${plural(summary.tasks_done, 'задача', 'задачи', 'задач')} ` +
        plural(summary.tasks_done, 'выполнена', 'выполнены', 'выполнено'),
    )
  }
  if (summary.geo_points > 0) {
    lines.push(
      `${summary.geo_points} ${plural(summary.geo_points, 'геометка', 'геометки', 'геометок')}`,
    )
  }

  return (
    <Card className="enter">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-lead font-bold">Итог дня</p>
          <p className="text-text-muted mt-1 text-note">{lines.join(' · ') || '—'}</p>
        </div>
        <Money value={summary.amount} currency={currency} size="lg" />
      </div>

      {summary.expenses_count > 0 && (
        <p className="text-text-muted mt-2 text-note">
          Расходы: {summary.expenses_count} шт. на{' '}
          <Money value={summary.expenses_amount} currency={currency} size="sm" tone="warm" />
        </p>
      )}

      {summary.tasks_left > 0 && (
        <p className="text-text-muted mt-1 text-note">
          Осталось {summary.tasks_left}{' '}
          {plural(summary.tasks_left, 'задача', 'задачи', 'задач')}
        </p>
      )}

      {summary.incidents > 0 && (
        <p className="text-warm-ink mt-1 text-note">
          {summary.incidents} {plural(summary.incidents, 'происшествие', 'происшествия', 'происшествий')}{' '}
          за день
        </p>
      )}

      {summary.shift_open ? (
        <p className="text-text-muted mt-3 text-caption">Смена ещё не закрыта</p>
      ) : (
        <p className="text-success mt-3 text-note font-medium">
          <Icon name="state-success" size={17} className="mr-1 align-[-3px]" />
          Всё под контролем
        </p>
      )}
    </Card>
  )
}
